import { RidangoReduxAction } from '@ridango/core';

import { tassign } from 'tassign';

import { PurchaseActions } from './purchase.actions';
import { CategoryProductDetails } from '../purchase.model';

export interface PurchaseState {
    steps: any[];
    activeStep: number;
    cardTypeMask: number;
    payloads: any[];
    flowCompleted: boolean;
    shopping_cart_id: number;
    product: any;
    relatedCard: any;
    medium: any;
    region_id: number;
    channel: string;
    sms: string;
    area: any;
    zones: any;
    language: string;
    invoiceLoading: boolean;
    notificationFormValidity: boolean;
    categoryProductDetails: CategoryProductDetails[];
}

export const PURCHASE_INITIAL_STATE: PurchaseState = {
    steps: [],
    activeStep: 0,
    cardTypeMask: null, // can not be 0 as will give a wrong result in bitmask
    payloads: [],
    flowCompleted: false,
    shopping_cart_id: null,
    product: null,
    relatedCard: null,
    medium: null,
    region_id: null,
    channel: null,
    sms: 'N',
    area: null,
    zones: null,
    language: '',
    invoiceLoading: false,
    notificationFormValidity: true,
    categoryProductDetails: []
};

/*
ENUMS_CARD_TYPE_MASK_UID=0  .....  pow(2, 0)
ENUMS_CARD_TYPE_MASK_PAN_HASH=1 .... = 2
ENUMS_CARD_TYPE_MASK_UTN=2 .... = 4
ENUMS_CARD_TYPE_MASK_PERSON_ID=3 .... = 8
ENUMS_CARD_TYPE_MASK_BARCODE=4 .... = 16
ENUMS_CARD_TYPE_MASK_GLOBAL=5 .... = 32
ENUMS_CARD_TYPE_MASK_QR=6 .... = 64
*/

// NB pasting currently here different payload examples
// 1. purchasing regular ticket to related card
// {"product_id":4317,"region_id":4096,"channel":"web","sms":"N","card_type":"N","related_card_ids":[41440],"shopping_cart_id":null}

// 2. purchasing regular ticket to card number
// {"product_id":4317,"region_id":4096,"channel":"web","sms":"N","card_type":"N","card_ids":["101-1257298147"],"shopping_cart_id":783261}

// 3. purchasing wallet fixed-sum to related card
// {"product_id":20516,"region_id":4096,"channel":"web","related_card_id":41440,"shopping_cart_id":783261} (should use batch add as previous ones)

// 4. purchasing wallet fixed-sum to card number
// {"product_id":20516,"region_id":4096,"channel":"web","card_id":"101-1257298147","card_type":"N","shopping_cart_id":783261} (should use batch add as previous ones)

// 5. purchasing wallet variable sum to related card
// {"product_id":4232,"region_id":4096,"channel":"web","related_card_id":41440,"price":222,"shopping_cart_id":783261} (should use batch add as previous ones)

// 6. purchasing wallet variable sum to card number
// {"product_id":4232,"region_id":4096,"channel":"web","card_id":"101-1257298147","card_type":"N","price":222,"shopping_cart_id":783261} (should use batch add as previous ones)

// 7. purchasing wallet fixed sum to voucher
// {"product_id":20516,"region_id":4096,"channel":"web","sms":"N","card_type":"S","emails":["hannes@ridango.com"],"shopping_cart_id":783261}

export const PURCHASE_STATE_KEY = 'purchase';

export function purchaseReducer(
    lastState: PurchaseState = PURCHASE_INITIAL_STATE,
    action: RidangoReduxAction
): PurchaseState {
    switch (action.type) {
        case PurchaseActions.SET_WALLET_PURCHASE_STEPS:
            return tassign(lastState, { steps: action.payload });
        case PurchaseActions.SET_PURCHASE_REGION:
            return tassign(lastState, { region_id: action.payload });
        case PurchaseActions.SET_CART_LANGUAGE:
            return tassign(lastState, { language: action.payload });
        case PurchaseActions.SET_WALLET_ACTIVE_PURCHASE_STEP:
            return tassign(lastState, { activeStep: action.payload });

        case PurchaseActions.PREPARE_CORPORATE_PAYLOADS:
            return tassign(lastState, {
                payloads: action.payload.cardIds.map((cardId, index) => {
                    return {
                        related_card_id: cardId,
                        card_type: action.payload.cardTypes[index],
                        company_id: action.payload.company_id,
                        channel: action.payload.channel
                    };
                })
            });

        case PurchaseActions.SET_WALLET_SELECTED_PRODUCT: {
            const needsZones = action.payload?.product_properties?.some((prop) => prop.code === 'DYNAMIC_LENGTH');
            return tassign(lastState, {
                cardTypeMask: action.payload.card_type_mask,
                product: action.payload,
                payloads:
                    lastState.payloads.length === 0
                        ? [
                              {
                                  product_id: action.payload.product_id,
                                  region_id: lastState.region_id,
                                  channel: lastState.channel,
                                  sms: lastState.sms,
                                  shopping_cart_id: lastState.shopping_cart_id,
                                  ...(needsZones && lastState.zones ? lastState.zones : {})
                              }
                          ]
                        : lastState.payloads.map((payload) => {
                              return {
                                  ...payload,
                                  product_id: action.payload.product_id,
                                  shopping_cart_id: lastState.shopping_cart_id
                              };
                          })
            });
        }

        case PurchaseActions.SET_WALLET_SELECTED_PRODUCT_QTY:
            return tassign(lastState, {
                payloads: action.payload
            });
        case PurchaseActions.SET_PRODUCT_NOTIFICATIONS:
            return tassign(lastState, {
                ...lastState,
                payloads: lastState.payloads.map((payload) => {
                    return { ...payload, ...action.payload };
                })
            });
        case PurchaseActions.RESET_PRODUCT_NOTIFICATIONS:
            return tassign(lastState, {
                ...lastState,
                notificationFormValidity: true,
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.notification_mask;
                    delete pl.mobile_phone;
                    pl.email = null;
                    return { ...pl };
                })
            });
        case PurchaseActions.SET_PRODUCT_NOTIFICATIONS_FORM_VALIDITY:
            return tassign(lastState, {
                ...lastState,
                notificationFormValidity: action.payload
            });
        case PurchaseActions.SET_WALLET_SELECTED_MEDIUM_RELATED_CARD:
            return tassign(lastState, {
                medium: action.payload,
                payloads:
                    lastState.payloads.length === 0
                        ? [
                              {
                                  related_card_id: action.payload.relatedCardId,
                                  card_type: action.payload.cardType,
                                  region_id: lastState.region_id,
                                  channel: lastState.channel,
                                  sms: lastState.sms,
                                  shopping_cart_id: lastState.shopping_cart_id
                              }
                          ]
                        : lastState.payloads.map((payload) => {
                              const pl = { ...payload };
                              delete pl.card_id;
                              delete pl.email;
                              return {
                                  ...pl,
                                  related_card_id: action.payload.relatedCardId,
                                  card_type: action.payload.cardType,
                                  shopping_cart_id: lastState.shopping_cart_id
                              };
                          })
            });
        case PurchaseActions.SET_WALLET_SELECTED_MEDIUM_TRAVEL_CARD:
            return tassign(lastState, {
                medium: action.payload,
                payloads:
                    lastState.payloads.length === 0
                        ? [
                              {
                                  card_id: action.payload.cardId,
                                  card_type: action.payload.cardType,
                                  region_id: lastState.region_id,
                                  channel: lastState.channel,
                                  sms: lastState.sms,
                                  shopping_cart_id: lastState.shopping_cart_id
                              }
                          ]
                        : lastState.payloads.map((payload) => {
                              const pl = { ...payload };
                              delete pl.related_card_id;
                              delete pl.email;
                              return {
                                  ...pl,
                                  card_id: action.payload.cardId,
                                  card_type: action.payload.cardType,
                                  shopping_cart_id: lastState.shopping_cart_id
                              };
                          })
            });

        case PurchaseActions.SET_WALLET_SELECTED_MEDIUM_EMAIL:
            return tassign(lastState, {
                medium: action.payload,
                payloads:
                    lastState.payloads.length === 0
                        ? [
                              {
                                  email: action.payload.email,
                                  card_type: action.payload.cardType,
                                  region_id: lastState.region_id,
                                  channel: lastState.channel,
                                  sms: lastState.sms,
                                  shopping_cart_id: lastState.shopping_cart_id
                              }
                          ]
                        : lastState.payloads.map((payload) => {
                              const pl = { ...payload };
                              delete pl.related_card_id;
                              delete pl.card_id;
                              return {
                                  ...pl,
                                  email: action.payload.email,
                                  card_type: action.payload.cardType,
                                  shopping_cart_id: lastState.shopping_cart_id
                              };
                          })
            });

        case PurchaseActions.SET_WALLET_SELECTED_SUM:
            return tassign(lastState, {
                payloads:
                    lastState.payloads.length === 0
                        ? [{ price: action.payload }]
                        : lastState.payloads.map((payload) => {
                              return { ...payload, price: action.payload };
                          })
            });
        case PurchaseActions.REMOVE_WALLET_SELECTED_SUM:
            return tassign(lastState, {
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.price;
                    return pl;
                })
            });

        case PurchaseActions.SET_PURCHASE_SHOPPING_CART_ID:
            return tassign(lastState, {
                shopping_cart_id: action.payload,
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    pl.shopping_cart_id = action.payload;
                    return pl;
                })
            });
        case PurchaseActions.SET_WALLET_IS_FLOW_COMPLETED:
            return tassign(lastState, { flowCompleted: action.payload });
        case PurchaseActions.CLEAR_PURCHASE_SELECTIONS:
            return tassign(lastState, {
                cardTypeMask: null, // can not be 0 as will give a wrong result in bitmask
                payloads: [],
                flowCompleted: false
            });
        case PurchaseActions.UNSET_PURCHASE_SHOPPING_CART_ID:
            return tassign(lastState, {
                shopping_cart_id: null,
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.shopping_cart_id;
                    return pl;
                })
            });
        case PurchaseActions.UNSET_TICKET_PURCHASE_PRODUCT:
            return tassign(lastState, {
                cardTypeMask: 0,
                product: null,
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.product_id;
                    return pl;
                })
            });
        case PurchaseActions.UNSET_TICKET_PURCHASE_MEDIUM:
            return tassign(lastState, {
                medium: null,
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.card_id;
                    delete pl.email;
                    delete pl.related_card_id;
                    delete pl.card_type;
                    return pl;
                })
            });
        case PurchaseActions.SET_TICKET_PURCHASE_AREA:
            return tassign(lastState, {
                area: { ...action.payload }
            });
        case PurchaseActions.SET_TICKET_PURCHASE_ZONES:
            return tassign(lastState, {
                zones: action.payload,
                payloads: lastState.payloads.map((payload) => {
                    return { ...payload, ...action.payload };
                })
            });
        case PurchaseActions.SET_TICKET_VALIDITY_DATES:
            return tassign(lastState, {
                payloads: lastState.payloads.map((payload) => {
                    return { ...payload, ...action.payload };
                })
            });
        case PurchaseActions.UNSET_TICKET_VALIDITY_DATES:
            return tassign(lastState, {
                payloads: lastState.payloads.map((payload) => {
                    const pl = { ...payload };
                    delete pl.starting;
                    delete pl.ending;
                    return pl;
                })
            });
        case PurchaseActions.RESET_WALLET_PURCHASE:
            return {
                ...lastState,
                ...PURCHASE_INITIAL_STATE
            };
        case PurchaseActions.SET_INVOICE_LOADING:
            return tassign(lastState, { invoiceLoading: action.payload });

        case PurchaseActions.REMOVE_CORPORATE_TRAVEL_CARD: {
            const payloads = lastState.payloads.filter((p) => p.related_card_id !== action.payload);
            return tassign(lastState, { payloads });
        }

        case PurchaseActions.SET_CHANNEL: {
            return tassign(lastState, {
                channel: action.payload
            });
        }

        case PurchaseActions.FETCH_CATEGORY_PRODUCT_DETAILS_SUCCESS: {
            return tassign(lastState, {
                categoryProductDetails: action.payload
            });
        }

        case PurchaseActions.FETCH_CATEGORY_PRODUCT_DETAILS_FAILED: {
            return tassign(lastState, {
                categoryProductDetails: null
            });
        }
    }

    return lastState;
}

export interface PurchasePartialStoreState {
    [PURCHASE_STATE_KEY]: PurchaseState;
}
